// ============================================================================
// AIRCRAFT ENTITY - Supply Drop Plane
// ============================================================================

import { EntityId, Vector2 } from '@/shared/types';
import { Vec2, generateId } from '@/shared/utils';

export enum AircraftType {
  SupplyDrop = 'supply_drop',  // B-2 Spirit - drops supply crates
  Bomber = 'bomber',           // B-52 Stratofortress - carpet bombing
}

export interface AircraftState {
  id: EntityId;
  type: AircraftType;
  position: Vector2;
  targetPosition: Vector2;
  speed: number;
  altitude: number; // Visual height above ground
  active: boolean;
  hasDropped: boolean;
  dropTime: number | null;
  bombsDropped: number; // For bombers: how many bombs dropped so far
}

export class Aircraft {
  public readonly id: EntityId;
  public readonly type: AircraftType;
  public position: Vector2;
  public prevPosition: Vector2;
  public startPosition: Vector2;
  public targetPosition: Vector2;
  public speed: number;
  public altitude: number; // Z-height for visual effect
  public active: boolean;
  public hasDropped: boolean;
  public dropTime: number | null;
  public bombsDropped: number; // For bombers
  private direction: Vector2;

  constructor(
    startPosition: Vector2,
    targetPosition: Vector2,
    speed: number,
    altitude: number,
    _startTime: number,
    type: AircraftType = AircraftType.SupplyDrop
  ) {
    this.id = generateId('aircraft');
    this.type = type;
    this.position = { ...startPosition };
    this.prevPosition = { ...startPosition };
    this.startPosition = { ...startPosition };
    this.targetPosition = { ...targetPosition };
    this.speed = speed;
    this.altitude = altitude;
    this.active = true;
    this.hasDropped = false;
    this.dropTime = null;
    this.bombsDropped = 0;

    // Calculate direction
    const dir = Vec2.sub(targetPosition, startPosition);
    this.direction = Vec2.normalize(dir);
  }

  update(dt: number, _time: number): void {
    if (!this.active) return;

    // Store previous position
    this.prevPosition = { ...this.position };

    // Move towards target
    const movement = Vec2.mul(this.direction, this.speed * dt / 1000);
    this.position = Vec2.add(this.position, movement);

    // Check if reached target
    const distToTarget = Vec2.distance(this.position, this.targetPosition);
    if (distToTarget < this.speed * dt / 1000) {
      this.active = false;
    }
  }

  getRotation(): number {
    return Math.atan2(this.direction.y, this.direction.x);
  }

  getVelocity(): Vector2 {
    // Calculate velocity based on direction and speed
    return Vec2.mul(this.direction, this.speed / 1000); // Convert to per-ms
  }

  toState(): AircraftState {
    return {
      id: this.id,
      type: this.type,
      position: this.position,
      targetPosition: this.targetPosition,
      speed: this.speed,
      altitude: this.altitude,
      active: this.active,
      hasDropped: this.hasDropped,
      dropTime: this.dropTime,
      bombsDropped: this.bombsDropped,
    };
  }

  static fromState(state: AircraftState, time: number): Aircraft {
    const aircraft = new Aircraft(
      state.position,
      state.targetPosition,
      state.speed,
      state.altitude,
      time,
      state.type
    );
    aircraft.hasDropped = state.hasDropped;
    aircraft.dropTime = state.dropTime;
    aircraft.bombsDropped = state.bombsDropped;
    aircraft.active = state.active;
    return aircraft;
  }
}
